import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError, of } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { jwtDecode } from 'jwt-decode';
import { HttpService } from './http.service';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private authUrl = environment.apiUrl; // Replace with your backend authentication URL
  private tokenExpirationTimer: any;
  private isAuthenticated$ = new BehaviorSubject<boolean>(false);
  isAuthenticated = this.isAuthenticated$.asObservable();

  userDataSubject = new BehaviorSubject<any | null>(null);
  userData$ = this.userDataSubject.asObservable();

  constructor(
    private http: HttpService,
    private router: Router,
    private storage: StorageService
  ) {
    this.autoLogin();
  }

  register(data: any): Observable<any> {
    // Registration no longer signs the user in directly — the account is
    // created unverified and a code is emailed for /auth/email/verify to
    // complete before any session is established.
    return this.http.post(`auth/register`, data).pipe(
      map((response: any) => {
        if (response.success) {
          return response;
        } else {
          throw response;
        }
      })
    );
  }

  socialLogin(data: any): Observable<any> {
    return this.http.post(`auth/social-login`, data).pipe(
      map((response: any) => {
        if (response.success) {
          this.handleAuthentication(response.user, response.authorisation);
          return response;
        } else {
          throw response;
        }
      })
    );
  }

  forgot(data: any): Observable<any> {
    return this.http.post(`auth/forgot-password`, data);
  }

  verifyEmail(data: any): Observable<any> {
    return this.http.post(`auth/email/verify`, data);
  }

  resendEmailCode(data: any): Observable<any> {
    return this.http.post(`auth/email/resend-code`, data);
  }

  resetPassword(data: any): Observable<any> {
    return this.http.post(`auth/reset-password`, data);
  }

  login(data: any): Observable<any> {
    return this.http.post<any>(`auth/login`, data).pipe(
      map((response: any) => {
        if (response.success) {
          this.handleAuthentication(response.user, response.authorisation);
          return response;
        } else {
          throw response;
        }
      }),
      catchError((error: any) => {
        // Handle the login error here
        return throwError(() => error); // Return the error as an observable
      })
    );
  }
  autologin(data: any): Observable<any> {
    return this.http.get<any>(`auth/auto-login?auto_login_token=${data}`).pipe(
      map((response: any) => {
        if (response.success) {
          this.handleAuthentication(response.user, response.authorisation);
          return response;
        } else {
          throw response;
        }
      }),
      catchError((error: any) => {
        // Handle the login error here
        return throwError(() => error); // Return the error as an observable
      })
    );
  }

  changePassword(data: any): Observable<any> {
    return this.http.post<any>(`auth/change-password`, data).pipe(
      map((response: any) => {
        if (response.success) {
          this.handleAuthentication(response.user, response.authorisation);
          return response;
        } else {
          throw response;
        }
      }),
      catchError((error: any) => {
        return throwError(() => error);
      })
    );
  }

  autoLogin() {
    try {
      const user = this.storage.getItem('user');
      const authorisation = this.storage.getItem('authorisation');

      if (user && authorisation) {
        return this.handleAuthentication(user, authorisation);
      }
      // this.logout();
    } catch (error) {
      this.storage.clear();
    }
  }

  logout() {
    this.clearAuthentication();
    this.router.navigate(['/login']);
  }

  handleAuthentication(user: any, authorisation: any) {
    let token = authorisation.token;

    this.storage.setItem('user', user);
    this.storage.setItem('authorisation', authorisation);
    this.storage.setItem('token', token);
    this.storage.setItem('auth-type', authorisation.type);

    // alert(JSON.stringify(token))

    this.isAuthenticated$.next(true);
  }

  private getExpirationDate(token: string): Date {
    const decoded: any = jwtDecode(token);
    const expirationDate: any = new Date(decoded.exp * 1000);
    return expirationDate;
  }

  get getUserData() {
    const user: any = this.storage.getItem('user');

    return user;
  }

  private clearAuthentication() {
    this.storage.removeItem('user');
    this.storage.removeItem('authorisation');
    this.storage.removeItem('token');
    this.storage.removeItem('auth-type');
    // clearTimeout(this.tokenExpirationTimer);
    this.isAuthenticated$.next(false);
  }

  private getUserDataFromToken() {
    const token: any = this.storage.getItem('token');
    if (token) {
      return jwtDecode(token);
    }
    return null;
  }

  updateUserData(userData: any) {
    if (userData) {
      this.storage.setItem('user', userData);
    }
  }

  // New method to update the token
  public updateToken(newToken: string) {
    if (newToken) {
      this.storage.setItem('token', newToken);
    }
  }
}
